import { useCallback, useContext, useEffect, useState } from 'react';
import { QuizContext } from 'src/views/Quiz/Quiz.context';
import { QUIZ_EVENT_TYPES, QUIZ_QUESTION_TYPES } from 'src/types/Quiz';
import { Badge, Input } from '@get-mosh/msh-shared';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/pro-solid-svg-icons';

const QuizPassWordField = () => {
    const {
        currentQuestion,
        handleSubmitAnswer,
        handleNavigateToQuestion,
        state,
        questionList,
    } = useContext(QuizContext);
    const { questionId, questionText, questionExplanation, answerList } =
        currentQuestion || {};
    const [pageTransitioning] = state.pageTransitioning;
    const [, setContinueVisible] = state.continueVisible;
    const [continueDisabled, setContinueDisabled] = state.continueDisabled;
    const [password, setPassword] = useState('');
    const [passwordTouched, setPasswordTouched] = useState(false);
    const [passwordVisible, setPasswordVisible] = useState(false);
    const togglePasswordVisibility = () => {
        setPasswordVisible(!passwordVisible);
    };

    const handleSubmit = useCallback(() => {
        if (password.length > 7 && !continueDisabled && questionId) {
            setContinueDisabled(true);
            handleSubmitAnswer(
                {
                    questionId: questionId,
                    questionText: questionText,
                    answerIds: [answerList[0].answerId],
                    answerTexts: [password],
                },
                (response) => {
                    handleNavigateToQuestion(response.currentQuestionId);
                },
            );
        } else {
            setPasswordTouched(true);
        }
    }, [
        password,
        continueDisabled,
        questionId,
        questionText,
        handleSubmitAnswer,
    ]);

    const getEmailFromLeadFormQuestion = (): string | null => {
        const leadFormQuestion = questionList.find(
            (question) =>
                question.questionType === QUIZ_QUESTION_TYPES.leadFormSSO,
        );
        return leadFormQuestion?.userAnswers[0].answerText || null;
    };

    useEffect(() => {
        window.addEventListener(QUIZ_EVENT_TYPES.continueClick, handleSubmit);

        return () =>
            window.removeEventListener(
                QUIZ_EVENT_TYPES.continueClick,
                handleSubmit,
            );
    }, [handleSubmit]);

    useEffect(() => {
        const handleClick = () => {
            if (password.length > 0) {
                setPasswordTouched(true);
            }
        };
        window.addEventListener('click', handleClick);

        return () => window.removeEventListener('click', handleClick);
    }, [password]);

    useEffect(() => {
        const handleKeyPress = (e: KeyboardEvent) => {
            if (e.key === 'Enter') handleSubmit();
        };
        window.addEventListener('keydown', handleKeyPress, false);

        return () =>
            window.removeEventListener('keydown', handleKeyPress, false);
    }, [handleSubmit]);

    useEffect(() => {
        if (!pageTransitioning) {
            setContinueVisible(true);
            setContinueDisabled(password === null || password.length < 8);
        }
    }, [password, pageTransitioning, setContinueDisabled, setContinueVisible]);

    return (
        <div className="flex items-center justify-center">
            <div className="flex w-full max-w-[300px] flex-col sm:max-w-[400px]">
                <div className="flex flex-col space-y-2 pb-4">
                    <h4 className="text-left">{questionText}</h4>
                    {questionExplanation ? (
                        <p className="text-grey-400">{questionExplanation}</p>
                    ) : null}
                    <Badge
                        text={getEmailFromLeadFormQuestion() || ''}
                        variant="tag-medium"
                        className="self-start rounded-lg"
                    />
                </div>

                <div className="relative">
                    <Input
                        data-testid="password-input"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        placeholder="Password"
                        type={passwordVisible ? 'text' : 'password'}
                        onFocus={() =>
                            passwordTouched && setPasswordTouched(false)
                        }
                        error={
                            passwordTouched && password.length < 8
                                ? ' Password must be at least 8 characters'
                                : ''
                        }
                        showFeedback={true}
                    />
                    <FontAwesomeIcon
                        icon={passwordVisible ? faEyeSlash : faEye}
                        className="text-grey-300 absolute right-3 top-5 -translate-y-1 transform cursor-pointer"
                        onClick={togglePasswordVisibility}
                    />
                </div>
            </div>
        </div>
    );
};

export default QuizPassWordField;
